import React, { useState } from "react";

import {
  Box,
  Button,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { boxShadow } from "swyft-react-core";
import EditIcon from "@material-ui/icons/Edit";
import { EQuoteStatus } from "../../api/Quote/EQuoteStatus";
import { QuoteDeduction } from "../../api/Quote/QuoteDeduction";
import { QuoteLineItem } from "../../api/Quote/QuoteLineItem";
import { Quote } from "../../api/Quote/Quote";
import {
  allowManagerEdits,
  deductionSort,
  quoteDeductionValue,
  hasAddons,
  hasQuoteSubscriptions,
  lineAddonSort,
  lineItemSort,
  quoteBalanceOutstanding,
  // quotePaymentsMade,
  quoteVat,
  calculateVatPaid,
  allowAdminEdits
} from "../../app/utils";

import { DeductionModal } from "./DeductionModal";
import { LineItemEditModal } from "./LineItemEditModal";
import {
  PermissionAdminWrite,
  PermissionDiscountWrite,
  PermissionQuoteApprovedLineItemWrite,
  PermissionQuoteLineItemWrite,
  PermissionQuoteWrite
} from "../../app/consts";
import { NewlineDisplay } from "../../components/NewlineDisplay";
import { refreshPayments } from "../../api/SwyftApi";
import { fetchQuote, syncQuote } from "../../store/QuoteSlice";
import { useDispatch } from "react-redux";
import QuoteRemoveDialog from "../quotePage/components/QuoteRemoveDialog";
import DeleteIcon from "@material-ui/icons/Delete";
import { useAuth } from "swyft-react-core";
import { DeductionApply } from "../../components/deduction/DeductionApply";
import { applyPromoCode } from "../../api/Quote/QuoteApi";
import { EQuoteDeductionStatus } from "../../api/Quote/EQuoteDeductionStatus";
import { TF } from "../../components/typography/TF";
import OverrideTheme from "../../components/OverrideTheme";
import { EQuotePurchaserType, Payment } from "../../api/Types";
import { PriceFormat } from "../../components/typography/PriceFormat";
import { NumericFormat } from "react-number-format";

const useStyles = makeStyles(() => ({
  root: {},
  table: {
    "& th, & td": {
      paddingLeft: "0",
      verticalAlign: "top"
    },
    "& th": {
      fontWeight: "bold"
    }
  },
  noBorder: {
    border: 0
  },
  center: {
    textAlign: "center"
  }
}));

interface Props {
  quote: Quote;
  onRemoveAddon: (addon: QuoteLineItem) => void;
}

interface State {
  deductionModalOpen: boolean;
  lineItemModalOpen: boolean;
  removeModelOpen: boolean;
  lineItem?: QuoteLineItem;
  deduction?: QuoteDeduction;
}

export const QuoteDetails: React.FC<Props> = ({ quote, onRemoveAddon }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [state, setState] = useState<State>({
    deductionModalOpen: false,
    lineItemModalOpen: false,
    removeModelOpen: false
  });

  const { hasPermission: checkPermission } = useAuth();

  var amountPayable = quote.price;
  var vat = quoteVat(quote);

  const editItem = function (itemId: string) {
    const lineItem = quote.items.find((i) => i.productId === itemId);
    if (lineItem) {
      setState({
        ...state,
        lineItemModalOpen: true,
        lineItem: lineItem,
        deduction: undefined
      });
      return;
    }
    const deduction = quote.deductions.find((i) => i.id === itemId);
    if (deduction) {
      setState({
        ...state,
        lineItemModalOpen: true,
        lineItem: undefined,
        deduction: deduction
      });
    }
  };

  const removeItem = function (itemId: string) {
    const lineItem = quote.addons?.find((i) => i.productId === itemId);
    if (lineItem) {
      setState({
        ...state,
        removeModelOpen: true,
        lineItem: lineItem
      });
      return;
    }
  };

  const handleRefreshPayment = async () => {
    await refreshPayments(quote.id)
      .then(() => {
        dispatch(fetchQuote(quote.id));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onApplyPromoCode = async (promoCode: string) => {
    await applyPromoCode(quote.id, promoCode);
    return dispatch(syncQuote(quote.id, false));
  };

  const paymentsMade: Payment[] = []; //quotePaymentsMade(quote);
  const outstandingBalance = quoteBalanceOutstanding(quote);

  const includeVat = quote.purchaserType === EQuotePurchaserType.Home;

  const subscriptionSection = hasQuoteSubscriptions(quote.subscriptions) && (
    <Box boxShadow={boxShadow} padding={3} mb={3}>
      <Typography variant="h2" paragraph>
        Servicing and Maintenance
      </Typography>
      <Box py={2} px={1}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell width={150}>Name</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Status</TableCell>
              <TableCell align="right" width={150}>
                Payment Type
              </TableCell>
              <TableCell align="right" width={100}>
                Price
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quote.subscriptions!.slice().map((i) => (
              <TableRow key={i.productId}>
                <TableCell>
                  <strong>{i.productCode}</strong>
                </TableCell>
                <TableCell>
                  <NewlineDisplay text={i.name} />
                </TableCell>
                <TableCell align="right">{i.status}</TableCell>
                <TableCell align="right">{i.paymentType}</TableCell>
                <TableCell align="right">
                  <PriceFormat value={i.price} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );

  return (
    <div>
      <OverrideTheme>
        <QuoteRemoveDialog
          open={state.removeModelOpen}
          onClose={() => setState({ ...state, removeModelOpen: false })}
          onRemove={onRemoveAddon}
          addon={state.lineItem}
        />
      </OverrideTheme>
      <DeductionModal
        quote={quote}
        isOpen={state.deductionModalOpen}
        onClose={() => setState({ ...state, deductionModalOpen: false })}
      />
      <LineItemEditModal
        quote={quote}
        item={state.lineItem}
        deduction={state.deduction}
        totalPrice={amountPayable}
        isOpen={state.lineItemModalOpen}
        onClose={() => setState({ ...state, lineItemModalOpen: false })}
      />

      <Box boxShadow={boxShadow} padding={3} mb={3}>
        <Typography variant="h2" paragraph>
          Quote Details
        </Typography>
        <Box display="flex" my={3}>
          <Typography variant="h2">Total</Typography>
          <Box ml={4}>
            <Typography variant="h2">
              <PriceFormat value={amountPayable} />
            </Typography>
          </Box>
        </Box>

        <hr />

        <Typography variant="h3">Breakdown</Typography>

        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell width={150}>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right" width={100}>
                Unit Price
              </TableCell>
              <TableCell align="right">VAT</TableCell>
              <TableCell align="right" width={100}>
                Amount
              </TableCell>
              {checkPermission(PermissionDiscountWrite) && (
                <TableCell width={40} className={classes.center}>
                  <EditIcon />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {quote.items
              .slice()
              .sort(lineItemSort)
              .map((i) => {
                let price = includeVat ? i.price : i.price - calculateVatPaid(i.price, i.vatRate || 0);
                return (
                  <TableRow key={i.productId}>
                    <TableCell>
                      <strong>{i.productCode}</strong>
                      <br />
                      {i.name}
                    </TableCell>
                    <TableCell>
                      <NewlineDisplay text={i.description} />
                    </TableCell>
                    <TableCell align="right">
                      <NumericFormat
                        value={i.quantity}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={2}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <PriceFormat value={price} />
                    </TableCell>
                    <TableCell align="right">{i.vatRate}%</TableCell>
                    <TableCell align="right">
                      <PriceFormat value={price * i.quantity} />
                    </TableCell>
                    {((checkPermission(PermissionQuoteApprovedLineItemWrite) && allowAdminEdits(quote)) ||
                      (checkPermission(PermissionQuoteLineItemWrite) && allowManagerEdits(quote))) && (
                      <TableCell>
                        <IconButton onClick={(_) => editItem(i.productId)}>
                          <EditIcon color="secondary" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            {hasAddons(quote.addons) &&
              quote
                .addons!.slice()
                .filter((i) => i.selected)
                .sort(lineAddonSort)
                .map((i) => {
                  let price = includeVat ? i.price : i.price - calculateVatPaid(i.price, i.vatRate || 0);
                  return (
                    <TableRow key={i.productId}>
                      <TableCell>
                        <strong>{i.productCode}</strong>
                        <br />
                        {i.name}
                      </TableCell>
                      <TableCell>
                        <NewlineDisplay text={i.name} />
                      </TableCell>
                      <TableCell align="right">
                        <PriceFormat value={i.quantity} />
                      </TableCell>
                      <TableCell align="right">
                        <PriceFormat value={price} />
                      </TableCell>
                      <TableCell align="right">{i.vatRate}%</TableCell>
                      <TableCell align="right">
                        <PriceFormat value={price * i.quantity} />
                      </TableCell>
                      {((checkPermission(PermissionQuoteWrite) && allowAdminEdits(quote)) ||
                        (checkPermission(PermissionQuoteWrite) && allowManagerEdits(quote))) && (
                        <TableCell>
                          <IconButton onClick={(_) => removeItem(i.productId)}>
                            <DeleteIcon color="secondary" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            {quote.deductions
              .slice()
              .sort(deductionSort)
              .map((d) => {
                let deductionValue = quoteDeductionValue(d, includeVat);
                return (
                  <TableRow key={d.id}>
                    <TableCell>
                      <strong>{d.code}</strong>
                      <br />
                      {d.name} {d.promoCode && <>({d.promoCode})</>}
                    </TableCell>
                    <TableCell>
                      {d.description}
                      {d.referralCode && <>({d.referralCode})</>}
                      {d.status !== EQuoteDeductionStatus.Applied && (
                        <TF component="span" variant="body2" bold>
                          &nbsp;Status: {d.status}
                        </TF>
                      )}
                    </TableCell>
                    <TableCell align="right">&nbsp;</TableCell>
                    <TableCell align="right">&nbsp; </TableCell>
                    <TableCell align="right">&nbsp;</TableCell>
                    <TableCell align="right">
                      <PriceFormat value={deductionValue} />
                    </TableCell>
                    {((checkPermission(PermissionDiscountWrite) && allowAdminEdits(quote)) ||
                      (checkPermission(PermissionDiscountWrite) && allowManagerEdits(quote))) && (
                      <TableCell>
                        <IconButton onClick={(_) => editItem(d.id)}>
                          <EditIcon color="secondary" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            {vat > 0 && !includeVat && (
              <TableRow>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
                <TableCell className={classes.noBorder}>
                  <Typography variant="subtitle2">Subtotal</Typography>
                </TableCell>
                <TableCell className={classes.noBorder} colSpan={4} align="right">
                  <Typography variant="subtitle2">
                    <PriceFormat value={amountPayable - vat} />
                  </Typography>
                </TableCell>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
              </TableRow>
            )}
            {vat > 0 && (
              <TableRow>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
                <TableCell className={classes.noBorder}>
                  <Typography variant="subtitle2">{includeVat ? <>Includes </> : null}VAT</Typography>
                </TableCell>
                <TableCell className={classes.noBorder} colSpan={4} align="right">
                  <Typography variant="subtitle2">
                    <PriceFormat value={vat} />
                  </Typography>
                </TableCell>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
              </TableRow>
            )}
            {paymentsMade.map((p, index) => (
              <TableRow key={index}>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
                <TableCell className={classes.noBorder}>
                  <Typography variant="subtitle2">
                    {p.amount === quote.depositRequirement ? "Deposit Paid" : "Payment"}
                  </Typography>
                </TableCell>
                <TableCell className={classes.noBorder} colSpan={4} align="right">
                  <Typography variant="subtitle2">
                    <PriceFormat value={p.amount * -1} />
                  </Typography>
                </TableCell>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className={classes.noBorder}>&nbsp;</TableCell>
              <TableCell className={classes.noBorder}>
                <Typography variant="h2">Total</Typography>
              </TableCell>
              <TableCell colSpan={4} align="right" className={classes.noBorder}>
                <Typography variant="h2">
                  <PriceFormat value={amountPayable} />
                </Typography>
              </TableCell>
              <TableCell className={classes.noBorder}>&nbsp;</TableCell>
            </TableRow>
            {paymentsMade.length > 0 && (
              <TableRow>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
                <TableCell className={classes.noBorder}>
                  <Typography variant="subtitle2">Balance Outstanding</Typography>
                </TableCell>
                <TableCell className={classes.noBorder} colSpan={4} align="right">
                  <Typography variant="subtitle2">
                    <PriceFormat value={outstandingBalance} />
                  </Typography>
                </TableCell>
                <TableCell className={classes.noBorder}>&nbsp;</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={3} className={classes.noBorder}>
                {((checkPermission(PermissionDiscountWrite) && allowAdminEdits(quote)) ||
                  (checkPermission(PermissionDiscountWrite) && allowManagerEdits(quote))) && (
                  <Box width="40%">
                    <DeductionApply onApply={onApplyPromoCode} />
                  </Box>
                )}
              </TableCell>
              <TableCell colSpan={3} align="right" className={classes.noBorder}>
                {((checkPermission(PermissionDiscountWrite) && allowAdminEdits(quote)) ||
                  (checkPermission(PermissionDiscountWrite) && allowManagerEdits(quote))) && (
                  <Button
                    onClick={(_) => {
                      setState({ ...state, deductionModalOpen: true });
                    }}
                    variant="contained"
                    color="secondary"
                  >
                    Add Deduction
                  </Button>
                )}
              </TableCell>
              <TableCell className={classes.noBorder}>&nbsp;</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={6} align="right" className={classes.noBorder}>
                {checkPermission(PermissionAdminWrite) &&
                  quote.status !== EQuoteStatus.Draft &&
                  quote.status !== EQuoteStatus.Pending && (
                    <Button onClick={handleRefreshPayment} variant="contained" color="secondary">
                      Refresh Payments
                    </Button>
                  )}
              </TableCell>
              <TableCell className={classes.noBorder}>&nbsp;</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      {subscriptionSection}
    </div>
  );
};
